import { useIntl } from 'react-intl';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Image,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useMemo } from 'react';
import { useAccessoryListByVehicle } from '@/core/domain/people/Accessory/Accessory.hooks';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { useDisclosure } from '@/hooks';
import UpdateWorkOrderModal from '@/components/Movements/WorkOrders/Update/UpdateWorkOrderModal';
import { colorTraslate } from '@/core/domain/people/Colors/Colors.types';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

function VehicleDetailsDescriptions() {
  const intl = useIntl();
  const {
    workorder: { data: workOrder },
  } = useWorkorderDetailContext();

  const [modalOpened, modalAction] = useDisclosure();

  const vehicleData = workOrder?.vehicle;
  const vehicleColor = vehicleData?.color?.split('.')[1];
  const hasComplementClosed = workOrder?.complements.some(
    (complement) => complement.status === WorkOrderComplementStatus.Closed,
  );

  const { data: vehicleAccessories } = useAccessoryListByVehicle(
    vehicleData?.id,
  );

  const accessoriesDescription = useMemo(() => {
    if (!vehicleAccessories?.length) return [];
    return vehicleAccessories.map((accessory) => accessory.description);
  }, [vehicleAccessories]);

  if (!vehicleData && hasComplementClosed) return null;

  if (!vehicleData) {
    return (
      <>
        <Alert
          type="warning"
          description={
            <div>
              <p>Nenhum veículo cadastrado para este orçamento.</p>
              <Button type="primary" onClick={modalAction.open}>
                Cadastrar
              </Button>
            </div>
          }
          style={{ marginBottom: 24 }}
        />

        <UpdateWorkOrderModal open={modalOpened} onCancel={modalAction.close} />
      </>
    );
  }

  const maximumVisibleAccessories = 6;

  return (
    <>
      <Row gutter={[16, 16]} align="middle">
        <Col span={24} xl={!!vehicleData?.image ? 18 : 24}>
          <Descriptions
            bordered
            column={{ xs: 8, md: 12, xl: 24 }}
            layout="vertical"
            className="workorder-descriptions"
            size="small"
            title="Dados do veículo"
          >
            <Descriptions.Item
              span={8}
              label={intl.formatMessage({ id: 'workOrders.domain.vehicle' })}
            >
              {vehicleData
                ? `${vehicleData?.brand} - ${vehicleData?.model}`
                : '-'}
            </Descriptions.Item>

            <Descriptions.Item
              span={8}
              label={intl.formatMessage({
                id: 'workOrders.domain.vehicle.licensePlate',
              })}
              style={{ whiteSpace: 'nowrap' }}
            >
              {vehicleData?.licensePlate || '-'}
            </Descriptions.Item>

            <Descriptions.Item
              span={8}
              label={intl.formatMessage({
                id: 'customers.domain.vehicle.chassi',
              })}
            >
              {vehicleData?.chassi || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Ano de fabricação/modelo">
              {vehicleData
                ? `${vehicleData?.yearFabrication}/${vehicleData?.yearModel}`
                : '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label="Cor"
              span={8}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Space>
                <button
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: vehicleColor,
                    border: 'none',
                    cursor: 'default',
                  }}
                />
                <Typography.Text>
                  {vehicleColor ? colorTraslate[vehicleColor] : ''}
                </Typography.Text>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Combustível">
              {vehicleData?.fuelName}
            </Descriptions.Item>

            <Descriptions.Item
              span={8}
              label={intl.formatMessage({
                id: 'customers.domain.vehicle.accessories',
              })}
            >
              <>
                {accessoriesDescription
                  .slice(0, maximumVisibleAccessories)
                  .map((item, index) => (
                    <span key={index}>
                      {item.length > 15 ? (
                        <Tooltip title={item}>{`${item.slice(
                          0,
                          15,
                        )}...`}</Tooltip>
                      ) : (
                        item
                      )}
                      {index < 3 && ', '}
                    </span>
                  ))}
                {accessoriesDescription.length > maximumVisibleAccessories ? (
                  <Tooltip
                    title={accessoriesDescription
                      .slice(maximumVisibleAccessories)
                      .join(', ')}
                  >
                    <Badge
                      count={`+${
                        accessoriesDescription.slice(maximumVisibleAccessories)
                          .length
                      }`}
                      style={{
                        backgroundColor: '#364068',
                        marginLeft: 8,
                      }}
                    />
                  </Tooltip>
                ) : (
                  'Não há acessórios incluídos'
                )}
              </>
            </Descriptions.Item>
          </Descriptions>
        </Col>

        {vehicleData?.image && (
          <Col span={24} lg={8} xl={6}>
            <Card
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: 300,
                maxWidth: 350,
                margin: 'auto',
              }}
              bodyStyle={{ padding: 0 }}
            >
              <Image
                src={vehicleData?.image}
                preview={false}
                style={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'cover',
                  padding: 5,
                }}
              />
            </Card>
          </Col>
        )}

        <Col span={24} lg={!!vehicleData?.image ? 16 : 24} xl={24}>
          <Descriptions
            bordered
            column={{ xs: 8, md: 12, xl: 24 }}
            layout="vertical"
            className="workorder-descriptions"
            size="small"
            title="Dados do motor"
          >
            <Descriptions.Item span={8} label="Cilindrada">
              {vehicleData?.engineCapacity || '-'}
            </Descriptions.Item>

            <Descriptions.Item
              span={8}
              label="Nº de válvulas"
              style={{ whiteSpace: 'nowrap' }}
            >
              {vehicleData?.valves || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Nº de cilindros">
              {vehicleData?.cylinders || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Nome do motor">
              {vehicleData?.engineName || '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label="Código do motor"
              span={8}
              style={{ whiteSpace: 'nowrap' }}
            >
              {vehicleData?.engineTechnicalCode || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Potência CV">
              {vehicleData?.horsePower?.map((item) => (
                <Tag color="processing">{item}</Tag>
              ))}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions
            bordered
            column={{ xs: 8, md: 12, xl: 24 }}
            layout="vertical"
            className="workorder-descriptions"
            size="small"
            title="Informações complementares"
            style={{ marginTop: 24 }}
          >
            <Descriptions.Item span={8} label="Linha">
              {vehicleData?.line || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Tipo de transmissão">
              {vehicleData?.transmissionType || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Entrada de ar">
              {vehicleData?.airIntake || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Sistema de freios">
              {vehicleData?.brakeSystem || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Sistema de direção">
              {vehicleData?.steeringSystem || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Sistema de tração">
              {vehicleData?.tractionSystem || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Sistema de injeção">
              {vehicleData?.injectionSystem || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Configuração do motor">
              {vehicleData?.engineConfiguration || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Freio traseiro">
              {vehicleData?.rearBrake || '-'}
            </Descriptions.Item>

            <Descriptions.Item span={8} label="Freio dianteiro">
              {vehicleData?.frontBrake || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </>
  );
}

export default VehicleDetailsDescriptions;
